import type { UiNode, UiNodeInputAttributes } from '@ory/client';
import { useId } from 'react';

import { Checkbox } from '@/components/Checkbox';
import { FormItem } from '@/components/FormItem';
import { HStack } from '@/components/Layout';
import { Label } from '@/components/ui/label';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';

/** 表示順序: チェックボックスは入力フィールドの後に配置 */
export const ORDER = 60;

type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * Checkbox inputノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるcheckbox inputフィールドをレンダリングします。
 * 主にIdentity Schemaのboolean型フィールド（例: 利用規約の同意）に使用されます。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#checkbox-input-node
 */
export function CheckboxInput({ node, disabled }: Props) {
  const { translateUiText } = useNodeTranslation();
  const attrs = node.attributes as UiNodeInputAttributes;
  const checkboxId = useId();

  // checkbox inputの場合はtype="checkbox"である必要がある
  if (attrs.type !== 'checkbox') {
    return null;
  }

  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;
  const errorMessages = node.messages
    ?.filter((msg) => msg.type === 'error')
    .map((msg) => translateUiText(msg));

  const isChecked = attrs.value === true || attrs.value === 'true';

  return (
    <FormItem
      fieldTitle={label}
      errorMessage={errorMessages?.[0]}
      fieldInput={
        <>
          <input
            type="hidden"
            name={attrs.name}
            value="false"
            disabled={disabled ?? attrs.disabled}
          />
          <HStack className="items-center gap-2">
            <Checkbox
              id={checkboxId}
              name={attrs.name}
              value="true"
              checked={isChecked}
              disabled={disabled ?? attrs.disabled}
              required={attrs.required}
            />
            {label && (
              <Label htmlFor={checkboxId} className="text-xs font-semibold">
                {label}
              </Label>
            )}
          </HStack>
        </>
      }
      className="w-full"
    />
  );
}
